<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal">
      <div class="modal-header">
        <h2>{{ previewMode ? 'Preview Sleep Content' : (editingId ? 'Edit Sleep Content' : 'Add New Sleep Content') }}</h2>
        <div class="header-actions">
          <button v-if="previewMode" @click="backToEdit" class="btn-secondary">
            <i class="icon-edit"></i> Back to Edit
          </button>
          <button @click="$emit('close')" class="close-btn">
            <i class="icon-close"></i>
          </button>
        </div>
      </div>
      
      <div class="modal-body">
        <!-- Title and basic fields -->
        <div class="form-section">
          <h3 class="section-title">Basic Information</h3>
          <div class="form-row">
            <div class="form-group">
              <label for="content-title">Title <span class="required">*</span></label>
              <input 
                id="content-title" 
                v-model="formData.title" 
                type="text" 
                placeholder="Enter content title"
              />
            </div>
            
            <div class="form-group">
              <label for="content-type">Type <span class="required">*</span></label>
              <select 
                id="content-type" 
                v-model="formData.type" 
              >
                <option value="bedtime">Bedtime Story</option>
                <option value="breathing">Breathing Exercise</option>
                <option value="sleep_frequency">Sleep Frequency</option>
                <option value="souds_of_nature">Sounds of Nature</option>
                <option value="brain_programming">Brain Programming</option>
              </select>
            </div>
          </div>
          
          <div class="form-group">
            <label for="content-description">Description</label>
            <div class="quill-wrapper">
              <QuillEditor
                v-model:content="formData.description"
                contentType="html"
                placeholder="Enter content description..."
                :toolbar="toolbarOptions"
                theme="snow"
                style="min-height: 120px;"
              />
            </div>
          </div>
        </div>
        
        <!-- Sleep content cover image -->
        <div class="form-section">
          <h3 class="section-title">Media Content</h3>
          
          <div class="form-row image-uploads">
            <div class="form-group">
              <label>Cover Image <span class="required">*</span></label>
              <div class="image-uploader">
                <FileUploader
                  :api-url="apiUrl"
                  :title="''"
                  :show-title="false"
                  :multiple="false"
                  :accept="'image/*'"
                  :show-files-list="false"
                  :auto-upload="true"
                  :image-preview-mode="true"
                  :support-text="'Recommended size: 800x600 pixels, JPG or PNG'"
                  :initial-files="formData.cover_photo ? [{ fullUrl: formData.cover_photo }] : []"
                  @files-uploaded="handleCoverImageUploaded"
                />
              </div>
            </div>

            <div class="form-group">
              <label>Main Photo <span class="required">*</span></label>
              <div class="image-uploader">
                <FileUploader
                  :api-url="apiUrl"
                  :title="''"
                  :show-title="false"
                  :multiple="false"
                  :accept="'image/*'"
                  :show-files-list="false"
                  :auto-upload="true"
                  :image-preview-mode="true"
                  :support-text="'Recommended size: 800x600 pixels, JPG or PNG'"
                  :initial-files="formData.main_photo ? [{ fullUrl: formData.main_photo }] : []"
                  @files-uploaded="handleMainPhotoUploaded"
                />
              </div>
            </div>
          </div>
          
          <!-- Content Type: Bedtime Story -->
          <div v-if="formData.type === 'bedtime'" class="content-type-section">
            <h4 class="content-type-title">📚 Bedtime Story Content</h4>
            <div class="form-group">
              <label>Audio File <span class="required">*</span></label>
              <div class="audio-uploader">
                <FileUploader
                  :api-url="apiUrl"
                  :title="'Upload Bedtime Story Audio'"
                  :show-title="true"
                  :accept="'audio/*'"
                  :auto-upload="true"
                  :gallery-mode="false"
                  :show-files-list="true"
                  :initial-files="formData.audioFiles || []"
                  :allow-delete="true"
                  :allow-preview="true"
                  :support-text="'Upload the bedtime story narration - Supported formats: MP3, WAV, OGG (max 20MB)'"
                  @files-uploaded="handleAudioFilesUploaded"
                />
              </div>
            </div>
          </div>

          <!-- Content Type: Breathing Exercise -->
          <div v-if="formData.type === 'breathing'" class="content-type-section">
            <h4 class="content-type-title">🧘‍♀️ Breathing Exercise Content</h4>
            <div class="form-group">
              <label>Video File <span class="required">*</span></label>
              <div class="video-uploader">
                <FileUploader
                  :api-url="apiUrl"
                  :title="'Upload Breathing Exercise Video'"
                  :show-title="true"
                  :accept="'video/*'"
                  :auto-upload="true"
                  :gallery-mode="false"
                  :show-files-list="true"
                  :initial-files="formData.videoFiles || []"
                  :allow-delete="true"
                  :allow-preview="true"
                  :support-text="'Upload the breathing exercise video - Supported formats: MP4, WebM (max 50MB)'"
                  @files-uploaded="handleVideoFilesUploaded"
                />
              </div>
            </div>
          </div>

          <!-- Content Type: Sleep Frequency, Sounds of Nature, Brain Programming -->
          <div v-if="formData.type !== 'bedtime' && formData.type !== 'breathing'" class="content-type-section">
            <h4 class="content-type-title">
              <span v-if="formData.type === 'sleep_frequency'">🎵 Sleep Frequency Content</span>
              <span v-else-if="formData.type === 'souds_of_nature'">🌿 Sounds of Nature Content</span>
              <span v-else-if="formData.type === 'brain_programming'">🧠 Brain Programming Content</span>
              <span v-else>🎬 Advanced Content</span>
            </h4>
            
            <div class="form-row">
              <div class="form-group">
                <label>Audio File <span class="required">*</span></label>
                <div class="audio-uploader">
                  <FileUploader
                    :api-url="apiUrl"
                    :title="'Upload Main Audio'"
                    :show-title="true"
                    :accept="'audio/*'"
                    :auto-upload="true"
                    :gallery-mode="false"
                    :show-files-list="true"
                    :initial-files="formData.audioFiles || []"
                    :allow-delete="true"
                    :allow-preview="true"
                    :support-text="'Upload the main audio track - Supported formats: MP3, WAV, OGG (max 20MB)'"
                    @files-uploaded="handleAudioFilesUploaded"
                  />
                </div>
              </div>

              <div class="form-group">
                <label>Voice Over <span class="required">*</span></label>
                <div class="voice-uploader">
                  <FileUploader
                    :api-url="apiUrl"
                    :title="'Upload Voice Over'"
                    :show-title="true"
                    :accept="'audio/*'"
                    :auto-upload="true"
                    :gallery-mode="false"
                    :show-files-list="true"
                    :initial-files="formData.voiceFiles || []"
                    :allow-delete="true"
                    :allow-preview="true"
                    :support-text="'Upload the voice over narration - Supported formats: MP3, WAV, OGG (max 20MB)'"
                    @files-uploaded="handleVoiceFilesUploaded"
                  />
                </div>
              </div>
            </div>

            <div class="form-group">
              <label>Video File <span class="required">*</span></label>
              <div class="video-uploader">
                <FileUploader
                  :api-url="apiUrl"
                  :title="'Upload Video Content'"
                  :show-title="true"
                  :accept="'video/*'"
                  :auto-upload="true"
                  :gallery-mode="false"
                  :show-files-list="true"
                  :initial-files="formData.videoFiles || []"
                  :allow-delete="true"
                  :allow-preview="true"
                  :support-text="'Upload the video content - Supported formats: MP4, WebM (max 50MB)'"
                  @files-uploaded="handleVideoFilesUploaded"
                />
              </div>
            </div>
          </div>
        </div>
        
        <!-- Form error message -->
        <div v-if="error" class="form-error-message">
          <i class="icon-error"></i>
          <span>{{ error }}</span>
        </div>
        
        <!-- Form actions -->
        <div class="form-actions">
          <button @click="$emit('close')" class="btn-secondary">Cancel</button>
          <button 
            @click="$emit('save', formData)" 
            class="btn-primary"
            :disabled="!formData.type || !formData.audio_url"
          >
            {{ editingId ? 'Update' : 'Save' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch } from 'vue'
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import FileUploader from '@/components/FileUploader.vue'

export default {
  name: 'SleepContentForm',
  components: {
    QuillEditor,
    FileUploader
  },
  props: {
    editingId: {
      type: Number,
      default: null
    },
    initialData: {
      type: Object,
      default: () => ({})
    },
    apiUrl: {
      type: String,
      required: true
    },
    error: {
      type: String,
      default: null
    }
  },
  emits: ['close', 'save', 'update:error'],
  setup(props, { emit }) {
    const previewMode = ref(false)
    
    const formData = ref({
      title: '',
      title_ar: '',
      type: 'bedtime',
      duration: 1,
      details: '',
      details_ar: '',
      cover_photo: '',
      audio_url: '',
      voice_over: '',
      video_over: '',
      main_photo: '',
      display_type: 'still',
      audioFiles: [],
      videoFiles: [],
      voiceFiles: []
    })
    
    // Set initial form data if editing
    if (props.initialData) {
      Object.keys(formData.value).forEach(key => {
        if (props.initialData[key] !== undefined) {
          formData.value[key] = props.initialData[key]
        }
      })
    }
    
    // Watch for type changes to handle field clearing
    watch(() => formData.value.type, (newType, oldType) => {
      // Clear relevant fields when switching types
      if (newType === 'bedtime') {
        // Bedtime only needs audio
        formData.value.voice_over = ''
        formData.value.video_over = ''
        formData.value.videoFiles = []
        formData.value.voiceFiles = []
      } else if (newType === 'breathing') {
        // Breathing only needs video
        formData.value.audio_url = ''
        formData.value.voice_over = ''
        formData.value.audioFiles = []
        formData.value.voiceFiles = []
      } else {
        // Other types need audio, voice, and video
        if (oldType === 'breathing') {
          formData.value.video_over = ''
          formData.value.videoFiles = []
        }
      }
    })
    
    const toolbarOptions = [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'script': 'sub' }, { 'script': 'super' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['clean'],
      ['link']
    ]
    
    const arabicToolbar = [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'script': 'sub' }, { 'script': 'super' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'font': [] }],
      [{ 'align': ['', 'right', 'center', 'justify'] }],
      ['clean'],
      ['link', 'image', 'video']
    ]
    
    // File upload handlers
    const handleCoverImageUploaded = (files) => {
      if (files && files.length > 0) {
        const imageUrl = files[0].fullUrl || files[0].url
        formData.value.cover_photo = imageUrl
      }
    }
    
    const handleMainPhotoUploaded = (files) => {
      if (files && files.length > 0) {
        const imageUrl = files[0].fullUrl || files[0].url
        formData.value.main_photo = imageUrl
      }
    }
    
    const handleAudioFilesUploaded = (files) => {
      if (files && files.length > 0) {
        if (!formData.value.audioFiles) {
          formData.value.audioFiles = []
        }
        
        const newAudioFiles = files.map(file => ({
          name: file.originalName || file.name,
          key: file.fileName || file.key,
          url: file.url || file.fullUrl,
          fullUrl: file.url || file.fullUrl,
          size: file.fileSize || file.size
        }))
        
        formData.value.audioFiles.unshift(...newAudioFiles)
        
        if (files[0]) {
          formData.value.audio_url = files[0].url || files[0].fullUrl
        }
      }
    }
    
    const handleVideoFilesUploaded = (files) => {
      if (files && files.length > 0) {
        if (!formData.value.videoFiles) {
          formData.value.videoFiles = []
        }
        
        const newVideoFiles = files.map(file => ({
          name: file.originalName || file.name,
          key: file.fileName || file.key,
          url: file.url || file.fullUrl,
          fullUrl: file.url || file.fullUrl,
          size: file.fileSize || file.size
        }))
        
        formData.value.videoFiles.unshift(...newVideoFiles)
        
        if (files[0]) {
          formData.value.video_over = files[0].url || files[0].fullUrl
        }
      }
    }
    
    const handleVoiceFilesUploaded = (files) => {
      if (files && files.length > 0) {
        if (!formData.value.voiceFiles) {
          formData.value.voiceFiles = []
        }
        
        const newVoiceFiles = files.map(file => ({
          name: file.originalName || file.name,
          key: file.fileName || file.key,
          url: file.url || file.fullUrl,
          fullUrl: file.url || file.fullUrl,
          size: file.fileSize || file.size
        }))
        
        formData.value.voiceFiles.unshift(...newVoiceFiles)
        
        if (files[0]) {
          formData.value.voice_over = files[0].url || files[0].fullUrl
        }
      }
    }
    
    // Preview mode functions
    const showPreview = () => {
      if (!formData.value.title || !formData.value.title_ar || !formData.value.type) {
        emit('update:error', 'Please fill in at least Title, Arabic Title, and Type before previewing')
        return
      }
      previewMode.value = true
    }
    
    const backToEdit = () => {
      previewMode.value = false
      emit('update:error', null)
    }
    
    return {
      formData,
      previewMode,
      toolbarOptions,
      arabicToolbar,
      showPreview,
      backToEdit,
      handleCoverImageUploaded,
      handleMainPhotoUploaded,
      handleAudioFilesUploaded,
      handleVideoFilesUploaded,
      handleVoiceFilesUploaded
    }
  }
}
</script> 